import dayjs from 'dayjs';
import produce from 'immer';
import { create } from 'zustand';

import { DropDownOption } from '@/types/common';
import { AmenityDto, PropertyType } from '@graphql/hooks-and-types';

import { LocationAvailableFilters, LocationFilter } from '../types';

export type StaticFilter = {
  unitType: string;
  selectedPropertyTypes: string[];
  selectedAmenities: string[];
  address: string;
  priceRange: string;
  sort: string;
  moveInDate: dayjs.Dayjs;
};

type DynamicFilterInput = { id: number; value: any; exact: boolean };

type FilterState = {
  state: {
    staticFilter: StaticFilter;
    priceRangeData: {
      min: number;
      max: number;
      options: DropDownOption[];
    };
    addressCoordinates: { lat: number; lng: number } | null;
    dynamicFilter: { [key: string]: DynamicFilterInput };
    locationType: string;
    availableAmenities: DropDownOption[];
    availableFilter: LocationAvailableFilters[];
    availableUnitTypes: DropDownOption[];
    availableUnitTypeFilters: LocationFilter[];
    availablePropertyTypes: DropDownOption[];
  };
  setAddressCoordinates: (coordinates: { lat: number; lng: number }) => void;
  setAvailableFilter: (serverFilters: LocationAvailableFilters[]) => void;
  setAvailableAmenities: (amenities: AmenityDto[]) => void;
  updateExactValue: (filterFieldName: string, value: boolean) => void;
  setAvailablePropertyTypes: (propertyTypes: PropertyType[]) => void;
  setAddress: (address: string) => void;
  updateStaticFilters: (filters: StaticFilter) => void;
  setLocationType: (type: string) => void;
  setUnitType: (type: string) => void;
  setAmenity: (types: string[]) => void;
  setMoveInDate: (date: dayjs.Dayjs) => void;
  updateFilterData: (filterData: LocationFilter, value: any) => void;
  resetFilter: () => void;
  getAddressCoordinates: () => { lat: number; lng: number } | null;
  getAvailableUnitTypes: () => DropDownOption[];
  getFilter: () => { [key: string]: DynamicFilterInput };
  getLocationType: () => string;
  getAvailableUnitTypeFilters: () => LocationFilter[];
  getAvailablePropertyTypes: () => DropDownOption[];
  getAvailableAmenities: () => DropDownOption[];
  getStaticFilters: () => StaticFilter;
  getPriceRangeData: () => { min: number; max: number; options: DropDownOption[] };
};

export const usePropertyFilters = create<FilterState>()((set: any, get: () => FilterState) => ({
  state: {
    staticFilter: {
      unitType: null,
      selectedPropertyTypes: [],
      selectedAmenities: [],
      address: '',
      priceRange: null,
      sort: 'default',
      moveInDate: null,
    },
    priceRangeData: {
      min: 0,
      max: 10000000,
      options: [],
    },
    addressCoordinates: null,
    availableFilter: [],
    availableAmenities: [],
    availableUnitTypes: [],
    availablePropertyTypes: [],
    dynamicFilter: {},
    availableUnitTypeFilters: [],
    locationType: null,
  },
  setAvailableFilter: (serverFilters) =>
    set(
      produce((draft: FilterState) => {
        draft.state.availableFilter = serverFilters || [];
        const availableFilters = [];
        serverFilters?.[0]?.filters?.forEach((filter) => {
          if (filter.fieldName?.toLocaleLowerCase() !== 'price') availableFilters.push(filter);
          else {
            draft.state.priceRangeData.min = filter.minValue;
            draft.state.priceRangeData.max = filter.maxValue;
            draft.state.priceRangeData.options = filter.options.map((option) => ({
              label: option.value as string,
              value: option.value,
            }));
          }
        });
        draft.state.availableUnitTypeFilters = availableFilters;
        draft.state.staticFilter.selectedPropertyTypes = [];
        draft.state.availablePropertyTypes =
          serverFilters?.[0].propertyTypes.map((propertyType) => ({ label: propertyType, value: propertyType })) || [];
        draft.state.staticFilter.selectedAmenities = [];
        draft.state.availableAmenities =
          serverFilters?.[0].amenities.map((amenity) => ({ label: amenity.name, value: amenity.id })) || [];
        const unitTypes =
          serverFilters?.map((locFilter) => ({
            label: locFilter.name,
            value: locFilter.name,
          })) || [];
        draft.state.availableUnitTypes = unitTypes;
        draft.state.staticFilter.unitType = unitTypes[0]?.value || null;
      }),
    ),
  setAvailablePropertyTypes: (propertyTypes) =>
    set(
      produce((draft: FilterState) => {
        draft.state.availablePropertyTypes = propertyTypes.map((propertyType) => ({
          label: propertyType,
          value: propertyType,
        }));
      }),
    ),
  setAddress: (address) =>
    set(
      produce((draft: FilterState) => {
        draft.state.staticFilter.address = address;
      }),
    ),
  setAddressCoordinates: (coordinates) =>
    set(
      produce((draft: FilterState) => {
        draft.state.addressCoordinates = coordinates;
      }),
    ),
  updateStaticFilters: (staticFilters) =>
    set(
      produce((draft: FilterState) => {
        draft.state.staticFilter = staticFilters;
      }),
    ),
  setUnitType: (type) =>
    set(
      produce((draft: FilterState) => {
        draft.state.staticFilter.unitType = type;
        const selectedUnitTypeData = draft.state.availableFilter.find((filter) => filter.name === type);
        const availableFilters = [];
        selectedUnitTypeData.filters.forEach((filter) => {
          if (filter.fieldName?.toLocaleLowerCase() !== 'price') availableFilters.push(filter);
          else {
            draft.state.priceRangeData.min = filter.minValue;
            draft.state.priceRangeData.max = filter.maxValue;
            draft.state.priceRangeData.options = filter.options.map((option) => ({
              label: option.value as string,
              value: option.value,
            }));
          }
        });
        draft.state.availableUnitTypeFilters = availableFilters;
        draft.state.availablePropertyTypes = selectedUnitTypeData.propertyTypes.map((propertyType) => ({
          label: propertyType,
          value: propertyType,
        }));
        draft.state.availableAmenities = selectedUnitTypeData.amenities.map((amenity) => ({
          label: amenity.name,
          value: amenity.id,
        }));
        draft.state.dynamicFilter = {};
      }),
    ),
  setMoveInDate: (date) =>
    set(
      produce((draft: FilterState) => {
        draft.state.staticFilter.moveInDate = date;
      }),
    ),
  setLocationType: (type) =>
    set(
      produce((draft: FilterState) => {
        draft.state.locationType = type;
      }),
    ),
  setAmenity: (types) =>
    set(
      produce((draft: FilterState) => {
        draft.state.staticFilter.selectedAmenities = types;
      }),
    ),
  updateFilterData: (filterData, value) =>
    set(
      produce((draft: FilterState) => {
        if (value) {
          draft.state.dynamicFilter[filterData.fieldName] = { value, id: filterData.id, exact: true };
        } else {
          delete draft.state.dynamicFilter[filterData.fieldName];
        }
      }),
    ),
  updateExactValue: (filterFieldName, value) =>
    set(
      produce((draft: FilterState) => {
        if (draft.state.dynamicFilter[filterFieldName]) {
          draft.state.dynamicFilter[filterFieldName].exact = value;
        }
      }),
    ),
  resetFilter: () =>
    set(
      produce((draft: FilterState) => {
        draft.state.dynamicFilter = {};
        draft.state.staticFilter.sort = 'default';
      }),
    ),
  setAvailableAmenities: (amenities) =>
    set(
      produce((draft: FilterState) => {
        draft.state.availableAmenities = amenities.map((amenity) => ({
          label: amenity.name,
          value: amenity.id,
        }));
      }),
    ),
  getAvailableUnitTypes: () => get().state.availableUnitTypes,
  getAddressCoordinates: () => get().state.addressCoordinates,
  getFilter: () => get().state.dynamicFilter,
  getLocationType: () => get().state.locationType || '',
  getAvailableUnitTypeFilters: () => get().state.availableUnitTypeFilters,
  getAvailablePropertyTypes: () => get().state.availablePropertyTypes,
  getAvailableAmenities: () => get().state.availableAmenities,
  getStaticFilters: () => get().state.staticFilter,
  getPriceRangeData: () => get().state.priceRangeData,
}));
